import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Skeleton, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useUpdateUserMutation, useGetUsersQuery, useGetUserGroupsQuery } from "../../store/services/user-service";
import { useLazyGetLocationsByTenancyIdQuery, useLazyGetCitiesByTenancyIdQuery, useLazyGetCompanyNamesByTenancyIdQuery,
    useLazyGetCountriesByTenancyIdQuery, useLazyGetStatesByTenancyIdQuery, useLazyGetDepartmentsByTenancyIdQuery
} from "../../store/services/tenancy.service";

UserDetails.propTypes = {
    user: PropTypes.object.isRequired,
    tenancyData: PropTypes.object.isRequired,
    tenancyBasicData: PropTypes.object.isRequired
};

export default function UserDetails(props) {
    const { user, tenancyData } = props;
    const [updateUser] = useUpdateUserMutation();

    const [userDetails, setUserDetails] = useState({ ...user });
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [loading, setLoading] = useState(false);
    const [getLocationsByTenancyId, { data: locations }] = useLazyGetLocationsByTenancyIdQuery();
    const [getDepartmentsByTenancyId, { data: departments }] = useLazyGetDepartmentsByTenancyIdQuery();
    const [getCompanyNamesByTenancyId, { data: companyNames }] = useLazyGetCompanyNamesByTenancyIdQuery();
    const [getStatesByTenancyId, { data: states }] = useLazyGetStatesByTenancyIdQuery();
    const [getCountriesByTenancyId, { data: countries }] = useLazyGetCountriesByTenancyIdQuery();
    const [getCitiesByTenancyId, { data: cities }] = useLazyGetCitiesByTenancyIdQuery();

    const { data: userGroups, isLoading: userGroupsLoading } = useGetUserGroupsQuery(
        { tenancyId: tenancyData?.tenancyId, userId: user?.id },
        { skip: !tenancyData?.tenancyId || !user?.id }
    );

    // Query params for fetching all users to populate line manager dropdown
    const queryParams = useMemo(() => ({
        perPage: 10000,
        page: 0,
        includes: "",
        query: "",
        searchTerm: "",
        tenancyId: tenancyData?.tenancyId || 0,
        adminFilter: false,
        extendedResults: false
    }), [tenancyData?.tenancyId]);

    // Fetch users for line manager dropdown
    const { data: usersData } = useGetUsersQuery(queryParams, {
        skip: !tenancyData?.tenancyId
    });

    // Process users data for line manager options
    const lineManagerOptions = useMemo(() => {
        if (!usersData?.data) return [];
        
        return usersData.data
            .filter(user => user.enabled && user.id !== userDetails.id) // Exclude disabled users and current user
            .map(user => ({
                id: user.id,
                name: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                email: user.email,
                label: `${user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email} (${user.email})`
            }));
    }, [usersData, userDetails.id]);

    const handleSave = async () => {
        setLoading(true);
        try {
            await updateUser({ userId: userDetails.id, user: userDetails })
                .unwrap()
                .then(() => {
                    setSnackbarMessage("User updated successfully");
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                })
                .catch(() => {
                    setSnackbarMessage("Error occurred while updating user");
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                });
        } catch (error) {
            setSnackbarMessage("Error occurred while updating user");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
        setLoading(false);
    };

    const handleAutocompleteChange = (field) => (event, value) => {
        if (typeof value === "string") {
            setUserDetails({
                ...userDetails,
                [field]: value
            });
        } else if (value && value.inputValue) {
            setUserDetails({
                ...userDetails,
                [field]: value.inputValue
            });
        } else {
            setUserDetails({
                ...userDetails,
                [field]: value ? value.name : null
            });
        }
    };

    const handleLineManagerChange = (event, value) => {
        if (value) {
            setUserDetails({
                ...userDetails,
                lineManagerId: value.id,
                lineManagerName: value.name,
                lineManagerEmail: value.email
            });
        } else {
            setUserDetails({
                ...userDetails,
                lineManagerId: null,
                lineManagerName: null,
                lineManagerEmail: null
            });
        }
    };

    const filterOptions = (options, params) => {
        const filtered = options.filter((option) => {
            if (params.inputValue) {
                return option.name.toLowerCase().includes(params.inputValue.toLowerCase());
            }

            return option;
        });

        if (params.inputValue !== "") {
            filtered.push({
                inputValue: params.inputValue,
                name: `Add "${params.inputValue}"`
            });
        }

        return filtered;
    };


    const getOptionLabel = (option) => {
        if (typeof option === "string") {
            return option;
        }

        if (option.inputValue) {
            return option.inputValue;
        }

        return option.name;
    };

    const renderOption = (props, option) => (
        <li {...props}>
            {option.name}
        </li>
    );

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <TextField
                    label="First Name"
                    value={userDetails.firstName}
                    size="medium"
                    fullWidth
                    onChange={(e) => setUserDetails({ ...userDetails, firstName: e.target.value })}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Last Name"
                    value={userDetails.lastName}
                    size="medium"
                    fullWidth
                    onChange={(e) => setUserDetails({ ...userDetails, lastName: e.target.value })}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Email"
                    value={userDetails.email}
                    size="medium"
                    fullWidth
                    disabled={ true }
                    onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
                    sx={{ mb: 2 }}
                />
                
                {/* Line Manager Dropdown */}
                <Box sx={{ mb: 2 }}>
                    <Autocomplete
                        id="line-manager-autocomplete"
                        size="medium"
                        fullWidth
                        options={lineManagerOptions}
                        value={userDetails.lineManagerId ? (lineManagerOptions.find(option => option.id === userDetails.lineManagerId) || {
                            id: userDetails.lineManagerId,
                            name: userDetails.lineManagerName,
                            email: userDetails.lineManagerEmail,
                            label: `${userDetails.lineManagerName} (${userDetails.lineManagerEmail})`
                        }) : null}
                        onChange={handleLineManagerChange}
                        getOptionLabel={(option) => option.label || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Line Manager"
                                placeholder="Select Line Manager"
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props}>
                                <Box>
                                    <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
                                    <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{option.email}</Box>
                                </Box>
                            </li>
                        )}
                    />
                </Box>

                {/* User Source Dropdown */}
                <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth size="medium">
                        <InputLabel id="user-source-label">User Source</InputLabel>
                        <Select
                            labelId="user-source-label"
                            id="user-source-select"
                            value={userDetails.userSource || "MANUAL"}
                            label="User Source"
                            onChange={(e) => setUserDetails({ ...userDetails, userSource: e.target.value })}
                        >
                            <MenuItem value="MANUAL">Manual</MenuItem>
                            <MenuItem value="STATIC">Static</MenuItem>
                            {(userDetails.userSource === "AD_SYNC" || user.userSource === "AD_SYNC") && <MenuItem value="AD_SYNC" disabled>Microsoft Entra Sync</MenuItem>}
                            {(userDetails.userSource === "GOOGLE_SYNC" || user.userSource === "GOOGLE_SYNC") && <MenuItem value="GOOGLE_SYNC" disabled>Google Workspace Sync</MenuItem>}
                        </Select>
                        <FormHelperText>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <Tooltip
                                    title={
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>User Source Types:</Typography>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Manual:</strong> Created in the Boxphish portal. User details may be updated if found in a Microsoft Entra or Google Workspace sync.</Typography>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Static:</strong> User will not be updated by any sync process, even if present in Microsoft Entra or Google Workspace groups.</Typography>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Microsoft Entra Sync:</strong> Created via Microsoft Entra synchronisation.</Typography>
                                            <Typography variant="body2"><strong>Google Workspace Sync:</strong> Created via Google Workspace synchronisation.</Typography>
                                        </Box>
                                    }
                                    placement="right"
                                    arrow
                                >
                                    <HelpOutlineIcon sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'help' }} />
                                </Tooltip>
                                <Box component="span">
                                    {userDetails.userSource === "AD_SYNC" && "This user was created via Microsoft Entra sync. Set to Static to prevent future sync updates."}
                                    {userDetails.userSource === "GOOGLE_SYNC" && "This user was created via Google Workspace sync. Set to Static to prevent future sync updates."}
                                    {userDetails.userSource === "MANUAL" && "This user was created manually. Details may be updated during sync if found in remote groups."}
                                    {userDetails.userSource === "STATIC" && "This user will not be modified by sync processes."}
                                    {!userDetails.userSource && "Select how this user should be managed."}
                                </Box>
                            </Box>
                        </FormHelperText>
                    </FormControl>
                </Box>

                {/* UPN Display (Read-only) */}
                {userDetails.upn && (
                    <TextField
                        label="UPN (Microsoft ID)"
                        value={userDetails.upn}
                        size="medium"
                        fullWidth
                        disabled
                        sx={{ mb: 2 }}
                        helperText="User Principal Name from Microsoft Entra"
                    />
                )}

                {tenancyData && (
                    <>
                        <Box
                            onClick={() => {
                                getLocationsByTenancyId({ tenancyId: tenancyData.tenancyId });
                            }}>
                            <Autocomplete
                                id="location-autocomplete"
                                size="medium"
                                fullWidth
                                freeSolo
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                value={userDetails.location}
                                options={locations || []}
                                sx={{ mb: 2 }}
                                onChange={handleAutocompleteChange("location")}
                                filterOptions={filterOptions}
                                getOptionLabel={getOptionLabel}
                                renderOption={renderOption}
                                renderInput={(params) => <TextField {...params} label="Location" />}
                            />
                        </Box>
                        <Box
                            onClick={() => {
                                getDepartmentsByTenancyId({ tenancyId: tenancyData.tenancyId });
                            }}>
                            <Autocomplete
                                id="department-autocomplete"
                                size="medium"
                                fullWidth
                                freeSolo
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                value={userDetails.department}
                                options={departments || []}
                                sx={{ mb: 2 }}
                                onChange={handleAutocompleteChange("department")}
                                filterOptions={filterOptions}
                                getOptionLabel={getOptionLabel}
                                renderOption={renderOption}
                                renderInput={(params) => <TextField {...params} label="Department" />}
                            />
                        </Box>
                        <Box
                            onClick={() => {
                                getCompanyNamesByTenancyId({ tenancyId: tenancyData.tenancyId });
                            }}>
                            <Autocomplete
                                id="companyName-autocomplete"
                                size="medium"
                                fullWidth
                                freeSolo
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                value={userDetails.companyName}
                                options={companyNames || []}
                                sx={{ mb: 2 }}
                                onChange={handleAutocompleteChange("companyName")}
                                filterOptions={filterOptions}
                                getOptionLabel={getOptionLabel}
                                renderOption={renderOption}
                                renderInput={(params) => <TextField {...params} label="Company Name" />}
                            />
                        </Box>
                        <Box
                            onClick={() => {
                                getCountriesByTenancyId({ tenancyId: tenancyData.tenancyId });
                            }}>
                            <Autocomplete
                                id="country-autocomplete"
                                size="medium"
                                fullWidth
                                freeSolo
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                value={userDetails.country}
                                options={countries || []}
                                sx={{ mb: 2 }}
                                onChange={handleAutocompleteChange("country")}
                                filterOptions={filterOptions}
                                getOptionLabel={getOptionLabel}
                                renderOption={renderOption}
                                renderInput={(params) => <TextField {...params} label="Country" />}
                            />
                        </Box>
                        <Box
                            onClick={() => {
                                getStatesByTenancyId({ tenancyId: tenancyData.tenancyId });
                            }}>
                            <Autocomplete
                                id="state-autocomplete"
                                size="medium"
                                fullWidth
                                freeSolo
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                value={userDetails.state}
                                options={states || []}
                                sx={{ mb: 2 }}
                                onChange={handleAutocompleteChange("state")}
                                filterOptions={filterOptions}
                                getOptionLabel={getOptionLabel}
                                renderOption={renderOption}
                                renderInput={(params) => <TextField {...params} label="State" />}
                            />
                        </Box>
                        <Box
                            onClick={() => {
                                getCitiesByTenancyId({ tenancyId: tenancyData.tenancyId });
                            }}>
                            <Autocomplete
                                id="city-autocomplete"
                                size="medium"
                                fullWidth
                                freeSolo
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                value={userDetails.city}
                                options={cities || []}
                                sx={{ mb: 2 }}
                                onChange={handleAutocompleteChange("city")}
                                filterOptions={filterOptions}
                                getOptionLabel={getOptionLabel}
                                renderOption={renderOption}
                                renderInput={(params) => <TextField {...params} label="City" />}
                            />
                        </Box>
                    </>
                )}

                {/* User Groups (read-only) */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                        User Groups
                    </Typography>
                    {userGroupsLoading ? (
                        <Skeleton variant="rounded" width="100%" height={40} />
                    ) : userGroups && userGroups.length > 0 ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {userGroups.map((group) => (
                                <Tooltip key={group.userGroupId} title={`Origin: ${group.origin || "unknown"}`} arrow>
                                    <Chip label={group.groupName} variant="outlined" />
                                </Tooltip>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            No groups
                        </Typography>
                    )}
                </Box>

                <Grid item xs={12}>
                    <Button onClick={handleSave} variant="contained" color="warning" sx={{ width: "50%" }}>
                        {loading ? <CircularProgress size={24} color="black" /> : "Update User"}
                    </Button>
                </Grid>
            </Grid>

            <Snackbar anchorOrigin={{ vertical: "top", horizontal: "center" }} open={snackbarOpen}
                      autoHideDuration={3000}
                      onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled"
                       sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Grid>
    );
};
